/**
 * Hook para la tarjeta cron de Flexy Stock Sync
 */
import { useState, useEffect, useCallback } from 'react'
import { FlexyStockService } from '../../services/cron/flexyStockService'
import { useCronTaskPolling } from './useCronTaskPolling'
import { useCronTaskCountdown } from './useCronTaskCountdown'
import {
  adaptFlexyStockStatusResponse,
  getFlexySnapshotCount,
} from '../../utils/flexyStockUtils'
import type { CronTaskConfig } from '../../config/cron/cronTasksConfig'
import type { CronTaskStatusData } from '../../types/cron/cronTasks'

interface UseFlexyStockTaskReturn {
  status: CronTaskStatusData | null
  count: number | null
  lastSyncedAt: string | null
  isLoading: boolean
  error: string | null
  countdown: {
    hours: number
    minutes: number
    seconds: number
    humanReadable: string
    isExpired: boolean
  }
  isJobRunning: boolean
  refetch: () => Promise<void>
}

export function useFlexyStockTask(config: CronTaskConfig): UseFlexyStockTaskReturn {
  const [status, setStatus] = useState<CronTaskStatusData | null>(null)
  const [count, setCount] = useState<number | null>(null)
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null)
  const [isJobRunning, setIsJobRunning] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchStatus = useCallback(async () => {
    try {
      setIsLoading(true)
      const raw = await FlexyStockService.getStatus()
      const adapted = adaptFlexyStockStatusResponse(raw)
      setStatus(adapted.task)
      setCount(getFlexySnapshotCount(raw))
      setLastSyncedAt(raw.last_synced_at ?? null)
      setIsJobRunning(raw.sync_all_job?.running === true)
      setError(null)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Error desconocido'
      setError(errorMessage)
      console.error(`Error obteniendo estado Flexy (${config.id}):`, err)
    } finally {
      setIsLoading(false)
    }
  }, [config.id])

  useCronTaskPolling({
    enabled: true,
    interval: config.pollingInterval || 30000,
    onPoll: fetchStatus,
  })

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  const countdown = useCronTaskCountdown({
    nextExecutionISO: status?.nextExecutionISO || null,
    enabled: !!status?.enabled && !isJobRunning,
  })

  const displayCountdown = isJobRunning
    ? {
        hours: 0,
        minutes: 0,
        seconds: 0,
        humanReadable: 'Sincronización en curso...',
        isExpired: true,
      }
    : countdown

  return {
    status,
    count,
    lastSyncedAt,
    isLoading,
    error,
    countdown: displayCountdown,
    isJobRunning,
    refetch: fetchStatus,
  }
}
